import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createStore, useStore } from '../app/store';
import { plural, two } from '../app/words';
import { stopsBucket, track } from '../data/analytics';
import { current, routeTitle } from '../data/model';
import { routeSheet, type RouteSheet } from '../data/routeSheet';
import { toast } from '../ui/Overlays';

/**
 * "Print the route sheet": a page that shows only on paper, through the browser's own print (Save as PDF is in its
 * dialog), with no PDF library. It's there while the print dialog is open (app.css, `#printsheet`).
 */
const printing = createStore<{ sheet: RouteSheet | null }>({ sheet: null });

export function printRoute(id: string) {
  const m = current.m;
  const r = m?.route(id);
  if (!m || !r) return toast('That route has gone');
  const sheet = routeSheet(r, m.raw(id) ?? m.data.history?.get(id) ?? null, { title: routeTitle(r, m.zone), courier: m.person(r.riderId)?.name ?? 'A courier', business: m.business?.name, zone: m.zone, now: m.now });
  printing.set({ sheet });
  track('route_sheet_printed', { state: r.state, stops: stopsBucket(sheet.stops.length), by_link: sheet.stops.filter((s) => !s.address).length });
}

export function PrintSheet() {
  const { sheet } = useStore(printing);
  const printed = useRef<RouteSheet | null>(null);
  useEffect(() => {
    if (!sheet || printed.current === sheet) return;
    printed.current = sheet;
    const title = document.title;
    // The name Save as PDF offers.
    document.title = sheet.fileName;
    addEventListener(
      'afterprint',
      () => {
        document.title = title;
        printing.set({ sheet: null });
      },
      { once: true },
    );
    window.print();
  }, [sheet]);
  if (!sheet) return null;
  return createPortal(
    <div id="printsheet">
      {sheet.business && <div className="ps-eyebrow">{sheet.business}</div>}
      <div className="ps-title">{sheet.title}</div>
      <div className="ps-line">
        {sheet.courier} · {sheet.day} · {plural(sheet.stops.length, 'stop')}
      </div>
      {sheet.noCustomers && <div className="ps-sub">A finished route’s record has no names, phones or notes.</div>}
      {sheet.anyByLink && <div className="ps-sub">Addresses Google found go as Maps links: Google’s terms don’t let Routr copy them out.</div>}
      <ol className="ps-stops">
        {sheet.stops.map((s) => (
          <li key={s.n}>
            <span className="ps-n">{two(s.n)}</span>
            <div className="ps-main">
              {(s.pickup || s.address) && <div className="ps-addr">{[s.pickup ? 'Pick up' : null, s.address].filter(Boolean).join(' · ')}</div>}
              {s.link && (
                <a className="ps-sub ps-link" href={s.link}>
                  {s.link}
                </a>
              )}
              {s.pin && <div className="ps-sub">The customer’s pin: {s.pin}</div>}
              {s.customer && <div>{s.customer}</div>}
              {s.note && <div className="ps-sub">{s.note}</div>}
              {s.outcome && <div className="ps-sub">{s.outcome}</div>}
            </div>
            <div className="ps-side">
              <div>{plural(s.packages, 'package')}</div>
              {s.amount && <div>{s.amount}</div>}
              {s.by && <div>{s.by}</div>}
            </div>
          </li>
        ))}
      </ol>
    </div>,
    document.body,
  );
}
